/**
 * Customer card — "Rezervacijos" (Reservations) tab (pr7-contract.md
 * §Frontend/2).
 *
 * Finvalda `productsreservations` are imported into the mirror but were
 * invisible in the customer card before this — this lists them the same way
 * "Atsiskaitymai" lists documents: paged, filterable, each row expandable to
 * its items.
 */
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Field, Label, Switch } from "@headlessui/react";
import clsx from "clsx";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { fetchReservations } from "services/customer-reservations.service";
import {
  CustomerReservation,
  CustomerReservationsResponse,
} from "types/CustomerReservations";
import { formatPrettyDate } from "utils/helperFunctions";

interface Props {
  companyId?: string;
  customerId?: string;
}

type TFunction = (key: string, options?: any) => string;

const PAGE_SIZE = 25;
const COLUMN_COUNT = 7; // expand chevron + the 6 data columns below

export default function CustomerReservations({ companyId, customerId }: Props) {
  const { t } = useTranslation();

  const [openOnly, setOpenOnly] = useState(false);
  const [page, setPage] = useState(1);

  const [data, setData] = useState<CustomerReservationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Same stale-response guard as customer-settlements.tsx: a slower earlier
  // request must not overwrite the result of a later one.
  const requestIdRef = useRef(0);

  const load = useCallback(
    (openOnlyValue: boolean, pageValue: number) => {
      if (!companyId || !customerId) return;
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(false);
      fetchReservations(companyId, customerId, {
        openOnly: openOnlyValue,
        page: pageValue,
        pageSize: PAGE_SIZE,
        // Always requested, same "keep it simple" call as the ledger's
        // withLines (pr7-contract.md §Frontend/1) — page size stays at 25.
        withItems: true,
      })
        .then((response) => {
          if (requestId !== requestIdRef.current) return;
          setData(response);
          setLoading(false);
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) return;
          setError(true);
          setLoading(false);
        });
    },
    [companyId, customerId]
  );

  useEffect(() => {
    load(openOnly, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, customerId, openOnly, page]);

  const handleOpenOnlyChange = (value: boolean) => {
    setOpenOnly(value);
    setPage(1);
  };

  const retry = () => load(openOnly, page);

  if (loading && !data) {
    return <ReservationsSkeleton />;
  }

  if (error && !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {t("reservations_load_failed", { defaultValue: "Could not load reservations." })}
        <button
          type="button"
          className="ml-3 rounded-md border border-red-300 bg-white px-3 py-1 text-red-700 hover:bg-red-100"
          onClick={retry}
        >
          {t("retry", { defaultValue: "Retry" })}
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="flex flex-col gap-5" data-testid="customer-reservations">
      <div className="flex items-center justify-end rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <Field className="flex items-center gap-2 max-md:min-h-11">
          <Switch
            checked={openOnly}
            onChange={handleOpenOnlyChange}
            className={clsx(
              openOnly ? "bg-gray-900" : "bg-gray-200",
              "relative inline-flex h-5 w-9 items-center rounded-full transition-colors max-md:h-6 max-md:w-11"
            )}
          >
            <span
              className={clsx(
                openOnly ? "translate-x-5 max-md:translate-x-6" : "translate-x-1",
                "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform max-md:h-4 max-md:w-4"
              )}
            />
          </Switch>
          <Label className="text-sm text-gray-700">
            {t("reservations_only_active", { defaultValue: "Active only" })}
          </Label>
        </Field>
      </div>

      <ReservationsTable
        // Remounts on filter/page change, dropping stale expanded rows along
        // with the stale data — same approach as customer-settlements.tsx.
        key={`${openOnly}-${data.page}`}
        items={data.items}
        total={data.total}
        page={data.page}
        pageSize={data.pageSize}
        loading={loading}
        t={t}
        onPrev={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => current + 1)}
      />
    </div>
  );
}

function ReservationsTable({
  items,
  total,
  page,
  pageSize,
  loading,
  t,
  onPrev,
  onNext,
}: {
  items: CustomerReservation[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  t: TFunction;
  onPrev: () => void;
  onNext: () => void;
}) {
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div
      className={clsx(
        "overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm",
        loading && "opacity-60"
      )}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500">
              <th className="w-8 px-2 py-2" aria-hidden="true" />
              <th className="px-3 py-2">{t("reservation_col_date", { defaultValue: "Date" })}</th>
              <th className="px-3 py-2">{t("reservation_col_due_date", { defaultValue: "Due" })}</th>
              <th className="px-3 py-2">{t("reservation_col_title", { defaultValue: "Title" })}</th>
              <th className="px-3 py-2">
                {t("reservation_col_series_number", { defaultValue: "Series / No." })}
              </th>
              <th className="px-3 py-2 text-right">
                {t("reservation_col_items", { defaultValue: "Items" })}
              </th>
              <th className="px-3 py-2">{t("reservation_col_status", { defaultValue: "Status" })}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={COLUMN_COUNT} className="px-3 py-8 text-center text-gray-400">
                  {t("no_data", { defaultValue: "No data" })}
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const isExpanded = expandedIds.has(item.id);
                return (
                  <Fragment key={item.id}>
                    <tr className="border-b border-gray-100 last:border-b-0">
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          aria-label={isExpanded ? t("collapse") : t("expand")}
                          aria-expanded={isExpanded}
                          className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-gray-100 max-md:h-11 max-md:w-11"
                          onClick={() => toggleExpanded(item.id)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {formatPrettyDate(item.createdAt)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {item.dueDate ? formatPrettyDate(item.dueDate) : "-"}
                      </td>
                      <td className="px-3 py-2">
                        {item.title || item.description || "-"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        {[item.series, item.number].filter(Boolean).join(" / ") || "-"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        {item.itemsCount} ({item.quantityTotal})
                      </td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <StatusBadge isExists={item.isExists} t={t} />
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-gray-100 last:border-b-0 bg-gray-50">
                        <td colSpan={COLUMN_COUNT} className="px-3 py-3">
                          <ReservationItems item={item} t={t} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2 text-sm text-gray-500">
        <span>
          {total === 0
            ? t("no_data", { defaultValue: "No data" })
            : t("pagination_range", {
                defaultValue: "{{start}}–{{end}} of {{total}}",
                start,
                end,
                total,
              })}
        </span>
        <div className="flex gap-1">
          <button
            type="button"
            aria-label={t("desk_page_prev", { defaultValue: "Previous" })}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 disabled:opacity-40 max-md:h-11 max-md:w-11"
            disabled={page <= 1}
            onClick={onPrev}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label={t("desk_page_next", { defaultValue: "Next" })}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 disabled:opacity-40 max-md:h-11 max-md:w-11"
            disabled={end >= total}
            onClick={onNext}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ isExists, t }: { isExists: boolean; t: TFunction }) {
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        isExists ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
      )}
    >
      {isExists
        ? t("reservation_status_active", { defaultValue: "Active" })
        : t("reservation_status_inactive", { defaultValue: "Inactive" })}
    </span>
  );
}

/**
 * A reservation's items, shown when its row is expanded (fetched eagerly
 * with `?withItems=1` — same "always request it" shortcut as the ledger's
 * document lines — so this never triggers a request of its own).
 */
function ReservationItems({ item, t }: { item: CustomerReservation; t: TFunction }) {
  const items = item.items || [];

  if (items.length === 0) {
    return (
      <span className="text-sm text-gray-400">
        {t("reservation_no_items", { defaultValue: "no items" })}
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="px-2 py-1">
              {t("reservation_items_col_item", { defaultValue: "Item" })}
            </th>
            <th className="px-2 py-1 text-right">
              {t("reservation_items_col_qty", { defaultValue: "Qty" })}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((line, index) => (
            <tr key={`${line.product}-${index}`} className="border-t border-gray-200">
              <td className="px-2 py-1">
                {[line.sku, line.title].filter(Boolean).join(" — ") || "-"}
              </td>
              <td className="px-2 py-1 text-right">{line.quantity}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {item.itemsTruncated && (
        <span className="text-xs text-gray-400">
          {t("reservation_items_truncated_note", { defaultValue: "…more items not shown" })}
        </span>
      )}
    </div>
  );
}

function ReservationsSkeleton() {
  return (
    <div className="flex flex-col gap-5" data-testid="customer-reservations-skeleton">
      <div className="h-14 animate-pulse rounded-xl border border-gray-200 bg-gray-50" />
      <div className="h-64 animate-pulse rounded-xl border border-gray-200 bg-gray-50" />
    </div>
  );
}
